import { v4 as uuid } from "uuid";
import type { Calibration } from "../classes/calibration";
import {
  calibrationStillMatchesDevice,
  isCompleteStoredCalibration,
} from "./calibration_core";

export interface CalibrationSaveMessage {
  type: "calibration-saved";
  user_id: string;
  tab_id: string;
  sequence: number;
  calibration: Calibration;
}

export function isCalibrationSaveMessage(value: unknown): value is CalibrationSaveMessage {
  if (!value || typeof value !== "object") return false;
  const message = value as CalibrationSaveMessage;
  return message.type === "calibration-saved" &&
    typeof message.user_id === "string" && message.user_id.length > 0 &&
    typeof message.tab_id === "string" && message.tab_id.length > 0 &&
    Number.isSafeInteger(message.sequence) && message.sequence > 0 &&
    isCompleteStoredCalibration(message.calibration) &&
    typeof message.calibration.id === "string" && message.calibration.id.length > 0 &&
    message.calibration.user_id === message.user_id &&
    (message.calibration.no_touch_screen === null ||
      typeof message.calibration.no_touch_screen === "boolean");
}

export function compareCalibrationSaves(a: CalibrationSaveMessage, b: CalibrationSaveMessage): number {
  return a.sequence - b.sequence || (a.tab_id < b.tab_id ? -1 : a.tab_id === b.tab_id ? 0 : 1);
}

/** Orders successful saves per user and screen, including saves made in this tab. */
export class CalibrationSync {
  private sequence = 0;
  private latest: CalibrationSaveMessage[] = [];
  private channel: BroadcastChannel | null = null;
  private listeners = new Set<(message: CalibrationSaveMessage) => void>();

  constructor(private app_id: string, readonly tab_id = uuid()) {}

  create(user_id: string, calibration: Calibration): CalibrationSaveMessage {
    return {
      type: "calibration-saved", user_id, tab_id: this.tab_id,
      sequence: ++this.sequence,
      calibration: JSON.parse(JSON.stringify(calibration)),
    };
  }

  accept(message: CalibrationSaveMessage): boolean {
    this.sequence = Math.max(this.sequence, message.sequence);
    const matches = (previous: CalibrationSaveMessage) =>
      previous.user_id === message.user_id &&
      calibrationStillMatchesDevice(previous.calibration, message.calibration);
    if (this.latest.some(previous => matches(previous) && compareCalibrationSaves(previous, message) >= 0)) {
      return false;
    }
    this.latest = this.latest.filter(previous => !matches(previous));
    this.latest.push(message);
    return true;
  }

  isLatestForUser(message: CalibrationSaveMessage): boolean {
    return !this.latest.some(previous => previous.user_id === message.user_id &&
      compareCalibrationSaves(previous, message) > 0);
  }

  publish(message: CalibrationSaveMessage) {
    this.channel?.postMessage(message);
  }

  subscribe(listener: (message: CalibrationSaveMessage) => void): () => void {
    this.listeners.add(listener);
    if (!this.channel && typeof BroadcastChannel !== "undefined") {
      this.channel = new BroadcastChannel(`opticalgym:${this.app_id}:calibration`);
      this.channel.onmessage = ({ data }) => {
        if (isCalibrationSaveMessage(data) && data.tab_id !== this.tab_id) {
          this.listeners.forEach(callback => callback(data));
        }
      };
    }
    return () => {
      this.listeners.delete(listener);
      if (!this.listeners.size) {
        this.channel?.close();
        this.channel = null;
      }
    };
  }
}

const applications = new Map<string, CalibrationSync>();
export function calibrationSync(app_id: string): CalibrationSync {
  if (!applications.has(app_id)) applications.set(app_id, new CalibrationSync(app_id));
  return applications.get(app_id)!;
}
